// Production DocumentDetailOverlay — a centered overlay that opens above any
// case workspace (Documents, Graph, Map, Insights, AI Agent) without
// unmount/navigation.
//
// This is a thin orchestrator: `useDocumentDetailOverlay` owns all IO (the
// document detail query + reprocess mutation), the pure leaf pieces live in
// `components/document-overlay/*`, and the data-fetching mid-tier pieces
// (`DetailRailBody`, `EntityAccordion`, `RelationshipAccordion`,
// `DocumentPreview`) are connected containers.

import { Check, Link2, X } from 'lucide-react'
import { type CSSProperties, useId, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ExtractionConfigModal } from '@/components/documents/ExtractionConfigModal'
import { toast } from '@/components/ui/sonner'
import type { DocumentFocus } from '@/features/document-overlay/focus'
import {
  REPROCESS_PIPELINE_LABEL,
  useDocumentDetailOverlay,
} from '@/features/document-overlay/hooks/useDocumentDetailOverlay'
import {
  type UseDocumentFaceSectionResult,
  useDocumentFaceSection,
} from '@/features/document-overlay/hooks/useDocumentFaces'
import {
  type UseDocumentPlatesResult,
  useDocumentPlates,
} from '@/features/document-overlay/hooks/useDocumentPlates'
import {
  type AutoPlay,
  type UseDocumentVideoResult,
  useDocumentVideo,
} from '@/features/document-overlay/hooks/useDocumentVideo'
import type { CaseDocumentDetail } from '@/services/api/cases'
import { isVideoFileType, isVideoUnavailable } from '@/utils/video-file'
import { DetailRailBody } from './DetailRailBody'
import { DocumentPreview } from './DocumentPreview'
import { FileIcon } from './FileIcon'
import { OverlayError } from './OverlayError'
import { OverlayLoading } from './OverlayLoading'
import { OverlayBackdrop, SegmentedTabs } from './primitives'
import { ReprocessButton, type ReprocessButtonProps } from './ReprocessButton'
import { StatusBadge } from './StatusBadge'

// Below this dialog width the rail (up to 520px) leaves the preview under ~400px,
// so the panes stack behind tabs instead. The dialog is 96vw wide: a 1024px
// viewport (983px) still gets the side-by-side layout, a portrait tablet does not.
const NARROW_BREAKPOINT = 920

// Shared style for the floating top-right icon buttons (copy-link, close).
const overlayIconButtonStyle: CSSProperties = {
  position: 'absolute',
  // Center in the preview's 44px toolbar, leaving 6px above and below.
  top: 6,
  zIndex: 2,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '8px',
  background: 'var(--accent)',
  border: '1px solid var(--border)',
  color: 'var(--foreground)',
  cursor: 'pointer',
}

// The scroll area of the details. It is the containing block of whatever is
// absolutely positioned inside it (`sr-only` text, mostly): anchored to the
// dialog instead, such an element sits below the dialog's clipped edge and gives
// the dialog a scroll range of its own, so scrolling a control into view could
// push the header and its close button out of reach.
const detailScrollStyle: CSSProperties = {
  position: 'relative',
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
}

// Size and offset of those buttons: a 36px tap target on the phone, the 32px
// toolbar button from `md` up.
const CLOSE_BUTTON_CLASS = 'right-3 size-9 md:size-8'
const COPY_LINK_BUTTON_CLASS = 'right-14 size-9 md:right-[52px] md:size-8'

// Copies the current URL — which carries `?doc=<id>` — so the open document can
// be shared as a deep link. Swaps to a check icon + toast on success.
function CopyLinkButton() {
  const { t } = useTranslation()
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      toast.success(t('documents.detail.linkCopied'))
      setTimeout(() => setCopied(false), 2000)
    } catch {
      toast.error(t('documents.detail.copyLinkFailed'))
    }
  }

  return (
    <button
      type="button"
      aria-label={t('documents.detail.copyLink')}
      title={t('documents.detail.copyLink')}
      onClick={handleCopy}
      className={COPY_LINK_BUTTON_CLASS}
      style={overlayIconButtonStyle}
    >
      {copied ? <Check size={15} /> : <Link2 size={15} />}
    </button>
  )
}

export function DocumentDetailOverlay({
  caseId,
  documentId,
  focus,
  onClose,
}: {
  caseId: string
  documentId: string
  /**
   * The face the overlay opened on, its page and the identity it is compared
   * against (PRD-0031 slices 03 and 08). Every field null when the overlay was
   * opened the ordinary way.
   */
  focus: DocumentFocus
  onClose: () => void
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [forceStacked, setForceStacked] = useState(false)
  const {
    file,
    isLoading,
    isError,
    refetch,
    reprocessOpen,
    setReprocessOpen,
    isReprocessing,
    reprocessSelectedDocs,
    handleReprocess,
  } = useDocumentDetailOverlay({ caseId, documentId })
  // One read, one selection: the rail and the preview are separate subtrees, so
  // the state that ties a row to its box has to live above both of them.
  const videoUnavailable = isVideoUnavailable(file?.file_type)
  const faces = useDocumentFaceSection(
    caseId,
    file && !videoUnavailable ? documentId : null,
    focus.faceId,
    file?.file_type
  )
  // What the player starts with: a card's "Play pass", else the window a search hit named.
  const autoPlay = useMemo<AutoPlay>(
    () => (focus.play && focus.faceId ? { faceId: focus.faceId } : focus.span),
    [focus]
  )
  // A recording's player and the rail share one selection and one "Play pass" (PRD-0074):
  // read once here, like the faces, and handed to both panes. Nothing is fetched for a photo.
  const video = useDocumentVideo(
    caseId,
    documentId,
    isVideoFileType(file?.file_type) && !videoUnavailable,
    faces,
    autoPlay
  )
  // The plates read in the recording, listed beside the faces (slice 07).
  const plates = useDocumentPlates(
    caseId,
    documentId,
    isVideoFileType(file?.file_type) && !videoUnavailable
  )

  const reprocess: ReprocessButtonProps = {
    onClick: () => setReprocessOpen(true),
    loading: isReprocessing,
    confirmedFaceCount: file?.confirmed_face_count,
    // A HUMINT capture is never reprocessed (ADR-0045); the API refuses it too.
    locked: file?.is_capture === true,
  }

  useLayoutEffect(() => {
    const el = containerRef.current
    if (!el) return
    const evaluate = () => setForceStacked(el.offsetWidth < NARROW_BREAKPOINT)
    evaluate()
    if (typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(evaluate)
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  const Layout = forceStacked ? StackedLayout : SideBySideLayout

  return (
    <>
      <OverlayBackdrop onClose={onClose}>
        <div
          ref={containerRef}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          // Phone: the whole screen, and opaque — glass would let the page behind
          // show through the text. From `md` up: the centred glass card.
          className="h-dvh w-full rounded-none bg-card md:h-[min(88vh,880px)] md:w-[min(96vw,1440px)] md:rounded-[16px] md:bg-(--glass-strong-bg)"
          style={{
            position: 'relative',
            border: '1px solid var(--glass-border)',
            boxShadow: 'var(--glass-shadow)',
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            color: 'var(--foreground)',
          }}
        >
          <CopyLinkButton />
          <button
            type="button"
            aria-label={t('documents.detail.close')}
            onClick={onClose}
            className={CLOSE_BUTTON_CLASS}
            style={overlayIconButtonStyle}
          >
            <X size={15} />
          </button>

          {isLoading ? (
            <OverlayLoading />
          ) : isError || !file ? (
            // No file and nothing loading (a paused or disabled read) is a failure
            // too: without this the overlay is an empty surface with two buttons.
            <OverlayError onRetry={refetch} />
          ) : videoUnavailable ? (
            <div className="flex flex-1 flex-col items-center justify-center gap-3 p-8 text-center">
              <h2 id={titleId} className="text-lg font-medium">
                {file.filename}
              </h2>
              <p className="text-sm text-muted-foreground">
                {t('documents.video.temporarilyUnavailable')}
              </p>
            </div>
          ) : (
            <Layout
              caseId={caseId}
              file={file}
              focus={focus}
              faces={faces}
              video={video}
              plates={plates}
              titleId={titleId}
              reprocess={reprocess}
            />
          )}
        </div>
      </OverlayBackdrop>

      <ExtractionConfigModal
        open={reprocessOpen}
        onOpenChange={setReprocessOpen}
        caseId={caseId}
        documentCount={1}
        pipelineName={REPROCESS_PIPELINE_LABEL}
        selectedDocuments={reprocessSelectedDocs}
        onRun={handleReprocess}
        isRunning={isReprocessing}
      />
    </>
  )
}

interface LayoutProps {
  caseId: string
  file: CaseDocumentDetail
  /** The face, page and identity the overlay opened on; all null without a focus. */
  focus: DocumentFocus
  /**
   * The document's faces, their selection and their decisions — read once by the
   * overlay so the rail's list and the preview's boxes share one selection and
   * one cache entry (slice 06).
   */
  faces: UseDocumentFaceSectionResult
  /** The recording's player state (PRD-0074): its copy, its tracks, the selected pass, "Play pass". */
  video: UseDocumentVideoResult
  /** The plates read in the recording and the officer's decisions on them (slice 07). */
  plates: UseDocumentPlatesResult
  titleId: string
  /** The reprocess button: its action, its spinner, the confirmed faces it warns about, and a capture's lock. */
  reprocess: ReprocessButtonProps
}

// ─── Variant A — detail rail dominant on the left, preview on the right ────

function SideBySideLayout({
  caseId,
  file,
  focus,
  faces,
  video,
  plates,
  titleId,
  reprocess,
}: LayoutProps) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(440px, 520px) 1fr',
        flex: 1,
        minHeight: 0,
      }}
    >
      <DetailRail
        caseId={caseId}
        file={file}
        focus={focus}
        faces={faces}
        video={video}
        plates={plates}
        titleId={titleId}
        reprocess={reprocess}
      />
      <div style={{ minWidth: 0, minHeight: 0, display: 'flex' }}>
        <DocumentPreview caseId={caseId} file={file} focus={focus} faces={faces} video={video} />
      </div>
    </div>
  )
}

// ─── Variant C — stacked: tabs centered, body swaps preview/details ─────────

function StackedLayout({
  caseId,
  file,
  focus,
  faces,
  video,
  plates,
  titleId,
  reprocess,
}: LayoutProps) {
  const { t } = useTranslation()
  const [tab, setTab] = useState<'preview' | 'details'>('preview')
  const setTabSafe = (v: string) => setTab(v as 'preview' | 'details')
  return (
    // `data-layout` lets the preview drop its own "Preview" bar here: the tab
    // above it already says so, and a phone has no height to spare.
    <div
      data-layout="stacked"
      className="group/overlay"
      style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}
    >
      <div
        style={{
          // The right padding clears both floating buttons (copy link + close).
          padding: '14px 104px 12px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          minWidth: 0,
        }}
      >
        <FileIcon kind={file.file_type} compact />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            id={titleId}
            style={{
              margin: 0,
              fontSize: 14,
              fontWeight: 600,
              color: 'var(--foreground)',
              lineHeight: 1.3,
              wordBreak: 'break-word',
              marginBottom: 4,
            }}
          >
            {file.filename}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
            <StatusBadge status={file.status} />
            <ReprocessButton {...reprocess} />
          </div>
        </div>
      </div>
      <div
        style={{
          padding: '4px 16px 14px',
          display: 'flex',
          justifyContent: 'center',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <SegmentedTabs
          tabs={[
            { value: 'preview', label: t('documents.tab.preview') },
            { value: 'details', label: t('documents.tab.details') },
          ]}
          active={tab}
          onChange={setTabSafe}
          size="md"
        />
      </div>
      <div style={{ flex: 1, minHeight: 0, display: 'flex' }}>
        {tab === 'preview' ? (
          <DocumentPreview caseId={caseId} file={file} focus={focus} faces={faces} video={video} />
        ) : (
          <div style={detailScrollStyle}>
            <DetailRailBody
              caseId={caseId}
              file={file}
              focus={focus}
              faces={faces}
              video={video}
              plates={plates}
              compact
            />
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Detail rail (Variant A) ────────────────────────────────────────────────

function DetailRail({
  caseId,
  file,
  focus,
  faces,
  video,
  plates,
  titleId,
  reprocess,
}: LayoutProps) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--card)',
        borderRight: '1px solid var(--border)',
        minHeight: 0,
      }}
    >
      <div
        style={{
          padding: '22px 60px 18px 22px',
          display: 'flex',
          gap: 14,
          alignItems: 'flex-start',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <FileIcon kind={file.file_type} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2
            id={titleId}
            style={{
              margin: 0,
              fontSize: 15,
              fontWeight: 600,
              color: 'var(--foreground)',
              lineHeight: 1.35,
              wordBreak: 'break-all',
              marginBottom: 10,
            }}
          >
            {file.filename}
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <StatusBadge status={file.status} />
            <ReprocessButton {...reprocess} />
          </div>
        </div>
      </div>
      <div style={detailScrollStyle}>
        <DetailRailBody
          caseId={caseId}
          file={file}
          focus={focus}
          faces={faces}
          video={video}
          plates={plates}
        />
      </div>
    </div>
  )
}
